import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Building, Droplets, Package, Factory, Space, Plus } from 'lucide-react';
import { PortfolioType, Product } from '@/types/portfolio';
import { EmptyItemsState } from '@/components/portfolio/EmptyItemsState';
import { FormulasList } from '@/components/portfolio/FormulasList';
import { FormulaWizard } from '@/components/portfolio/FormulaWizard';
import { ProductsList } from '@/components/portfolio/ProductsList';
import { BuildingsList } from '@/components/portfolio/BuildingsList';
import { EditProductDialog } from '@/components/portfolio/EditProductDialog';
import { ViewSwitcher } from '@/components/portfolio/ViewSwitcher';
import { Button } from '@/components/ui/button';
import { AddItemDialog } from '@/components/portfolio/AddItemDialog';
import { useProducts } from '@/hooks/useProducts';
import { useBuildings } from '@/hooks/useBuildings';

interface PortfolioTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  portfolioType: PortfolioType;
  portfolioId: string;
}

export const PortfolioTabs = ({
  activeTab,
  setActiveTab,
  portfolioType,
  portfolioId
}: PortfolioTabsProps) => {
  const [formulaWizardOpen, setFormulaWizardOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productsView, setProductsView] = useState<'card' | 'table'>('card');

  // Use appropriate hooks based on portfolio type
  const { 
    products, 
    isLoading: productsLoading, 
    createProduct, 
    updateProduct,
    deleteProduct 
  } = useProducts(portfolioId);
  
  const { 
    buildings, 
    isLoading: buildingsLoading, 
    createBuilding, 
    deleteBuilding 
  } = useBuildings(portfolioId);

  // Get the appropriate label for the add button based on the active tab
  const getAddButtonLabel = () => {
    switch (activeTab) {
      case 'products':
        return 'Add Product';
      case 'formulas':
        return 'Add Formula';
      case 'facilities':
        return 'Add Facility';
      case 'buildings':
        return 'Add Building';
      case 'spaces':
        return 'Add Space';
      default:
        return 'Add Item';
    }
  };

  // Handle add button click based on active tab
  const handleAddClick = () => {
    if (activeTab === 'formulas') {
      setFormulaWizardOpen(true);
    } else {
      setAddItemDialogOpen(true);
    }
  };

  // Handle adding items based on tab
  const handleAddItem = (item: { name: string; description: string | null }) => {
    if (activeTab === 'products') {
      createProduct.mutate(item);
    } else if (activeTab === 'buildings') {
      createBuilding.mutate(item);
    }
  };

  // Handle deleting items based on tab
  const handleDeleteItem = (id: string) => {
    if (activeTab === 'products') {
      deleteProduct.mutate(id);
    } else if (activeTab === 'buildings') {
      deleteBuilding.mutate(id);
    }
  };

  // Handle editing products
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setEditProductDialogOpen(true);
  };

  const handleSaveProduct = (updates: { name: string; description: string | null; manufacturer?: string }) => {
    if (editingProduct) {
      updateProduct.mutate(
        { id: editingProduct.id, updates },
        {
          onSuccess: () => {
            setEditProductDialogOpen(false);
            setEditingProduct(null);
          }
        }
      );
    }
  };

  // Renders tabs based on portfolio type
  const renderTabs = () => {
    if (portfolioType === 'formulated_products') {
      return (
        <>
          <TabsTrigger value="products" className="flex-1">
            <Package className="h-4 w-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="formulas" className="flex-1">
            <Droplets className="h-4 w-4 mr-2" />
            Formulas
          </TabsTrigger>
          <TabsTrigger value="facilities" className="flex-1">
            <Factory className="h-4 w-4 mr-2" />
            Facilities
          </TabsTrigger>
        </>
      );
    } else if (portfolioType === 'buildings') {
      return (
        <>
          <TabsTrigger value="buildings" className="flex-1">
            <Building className="h-4 w-4 mr-2" />
            Buildings
          </TabsTrigger>
          <TabsTrigger value="spaces" className="flex-1">
            <Space className="h-4 w-4 mr-2" />
            Spaces
          </TabsTrigger>
        </>
      );
    } else {
      // Default tabs for other portfolio types
      return (
        <>
          <TabsTrigger value="products" className="flex-1">
            <Package className="h-4 w-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="formulas" className="flex-1">
            <Droplets className="h-4 w-4 mr-2" />
            Formulas
          </TabsTrigger>
          <TabsTrigger value="facilities" className="flex-1">
            <Building className="h-4 w-4 mr-2" />
            Facilities
          </TabsTrigger>
        </>
      );
    }
  };

  // Renders content based on portfolio type and active tab
  const renderContent = () => {
    // Products content
    const renderProductsContent = (
      <TabsContent value="products" className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Products</h3>
          <div className="flex items-center gap-3">
            <ViewSwitcher 
              view={productsView} 
              onViewChange={setProductsView}
            />
            <Button onClick={handleAddClick} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Product
            </Button>
          </div>
        </div>
        
        {productsLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading products...</p>
          </div>
        ) : products && products.length > 0 ? (
          <ProductsList 
            products={products} 
            onDelete={handleDeleteItem} 
            onEdit={handleEditProduct}
            view={productsView}
          />
        ) : (
          <EmptyItemsState
            portfolioType={portfolioType}
            onAddClick={handleAddClick}
          />
        )}
      </TabsContent>
    );

    // Buildings content
    const renderBuildingsContent = (
      <TabsContent value="buildings" className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Buildings</h3>
          <Button onClick={handleAddClick} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Building
          </Button>
        </div>
        
        {buildingsLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading buildings...</p>
          </div>
        ) : buildings && buildings.length > 0 ? (
          <BuildingsList buildings={buildings} onDelete={handleDeleteItem} />
        ) : (
          <EmptyItemsState
            portfolioType={portfolioType}
            onAddClick={handleAddClick}
          />
        )}
      </TabsContent>
    );

    // Generic "Coming Soon" content
    const renderComingSoonContent = (tabValue: string, title: string) => (
      <TabsContent value={tabValue}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{title}</h3>
          <Button onClick={handleAddClick} className="gap-2">
            <Plus className="h-4 w-4" />
            {getAddButtonLabel()}
          </Button>
        </div>
        
        <div className="bg-muted/50 p-8 rounded-lg border border-dashed text-center">
          <h3 className="text-lg font-medium mb-2">{title} Coming Soon</h3>
          <p className="text-muted-foreground">
            This section will allow you to manage {title.toLowerCase()} and their details.
          </p>
        </div>
      </TabsContent>
    );

    // Formula tab content
    const renderFormulasContent = (
      <TabsContent value="formulas" className="space-y-4">
        <FormulasList 
          portfolioId={portfolioId}
          onAddClick={() => setFormulaWizardOpen(true)}
        />
      </TabsContent>
    );

    if (portfolioType === 'formulated_products') {
      return (
        <>
          {renderProductsContent}
          {renderFormulasContent}
          {renderComingSoonContent('facilities', 'Facilities')}
        </>
      );
    } else if (portfolioType === 'buildings') {
      return (
        <>
          {renderBuildingsContent}
          {renderComingSoonContent('spaces', 'Spaces')}
        </>
      );
    } else {
      return (
        <>
          {renderProductsContent}
          {renderComingSoonContent('formulas', 'Formulas')}
          {renderComingSoonContent('facilities', 'Facilities')}
        </>
      );
    }
  };

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full mb-4">
          {renderTabs()}
        </TabsList>
        
        {renderContent()}
      </Tabs>
      
      <FormulaWizard
        portfolioId={portfolioId}
        isOpen={formulaWizardOpen}
        onClose={() => setFormulaWizardOpen(false)}
      />
      
      <AddItemDialog
        portfolio={{ portfolio_type: portfolioType } as any}
        onAdd={handleAddItem}
        open={addItemDialogOpen}
        onOpenChange={setAddItemDialogOpen}
      />

      {editingProduct && (
        <EditProductDialog
          product={editingProduct}
          open={editProductDialogOpen}
          onOpenChange={setEditProductDialogOpen}
          onSave={handleSaveProduct}
          isLoading={updateProduct.isPending}
        />
      )}
    </>
  );
};
